import { iEmployee } from "../../../cached-data/types"
import { CicoDiffExceptionSettings, EmployeeWorkShiftStartStopTime, TkCellData, HoursExceptionSettings, ShiftsBreaksExceptionSettings } from "../../../common/types"
import { tContext } from "../types"
import { Dispatch } from "react"
import { getFlagEnabled } from "../../../getFlagValue"
import { TkRowData } from "../../../common/types"

/**
 * Activate an exception filter
 * @param toggleFn
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const activateException = (toggleFn: () => Record<string, any>) => {
    return (context: tContext) => {
        const dateCols = context.columnApi!.getAllGridColumns()
        context.gridApi!.setFilterModel({
            [dateCols.at(-1)!.getId()]: {},
        })
        return (dispatch: Dispatch<any>): any => dispatch(toggleFn())
    }
}

/**
 * Determine whether the data from a cell constitutes an exception for the CI/CO time
 * @param data Cell data
 * @param exceptionValue The exception value and units
 */
const hoursExceedCico = (data: TkCellData, exceptionValue: CicoDiffExceptionSettings) => {
    const enteredHours = data.cicoTime
    // If we're rounding cico totals, the meal duration is already baked in to the clocked_in_duration.
    const cicoHours =
        getFlagEnabled("WA-8941-exceptions-fixes") && !getFlagEnabled("RN-2134-round-cico-totals")
            ? (data.clocked_in_duration || 0) / 60 - (data.meal_duration || 0) / 60
            : (data.clocked_in_duration || 0) / 60
    if (!enteredHours) return false
    const value = parseFloat(exceptionValue.value)
    if (exceptionValue.units === "hours") return Math.abs(enteredHours - cicoHours) > value
    else {
        if (!cicoHours || !enteredHours) return true
        if (enteredHours > cicoHours) return Math.abs(1 - cicoHours / enteredHours) > value / 100
        else return Math.abs(1 - enteredHours / cicoHours) > value / 100
    }
}

/**
 * Determine whether a cell constitutes a CI/CO exception
 * @param exceptionValue The value and units of the exception
 * @param cellData The data for the cell
 * @param employee Optional employee object. There are different circumstances depending on where
 * this function gets called. If we have the employee, it's a little simpler as the totals have
 * already been stored there
 * @param isTotalCell Whether the cell is the total cell at the end of the week
 */
export const isCicoException = (exceptionValue: CicoDiffExceptionSettings, cellData?: TkRowData, employee?: iEmployee, isTotalCell?: boolean): boolean => {
    if (isTotalCell) return false
    let hoursExceed = false
    let editedByOther = false
    let anyEdits = false

    if (!employee && cellData) {
        if (!cellData.byDate || !Object.keys(cellData.byDate).length) {
            // Ignore cells with no cico data (if the settings dictate)
            if (exceptionValue.ignoreNoCico && !cellData.clocked_in_duration) return false
            hoursExceed = hoursExceedCico(cellData, exceptionValue)
            editedByOther = cellData.edited_by_other > 0
            anyEdits = cellData.version_count > cellData.item_count
        } else {
            // Ignore cells with no cico data (if the settings dictate)
            if (exceptionValue.ignoreNoCico && !Object.values(cellData.byDate).some(data => data.clocked_in_duration)) return false
            hoursExceed = Object.values(cellData.byDate).some(data => {
                return getFlagEnabled("WA-8941-exceptions-fixes") && exceptionValue.ignoreNoCico && !data.clocked_in_duration ? false : hoursExceedCico(data, exceptionValue)
            })
            editedByOther = Object.values(cellData.byDate).some(data => data.edited_by_other > 0)
            anyEdits = Object.values(cellData.byDate).some(data => data.version_count > data.item_count)
        }
    } else if (employee) {
        const { entered_time_duration, working_duration } = employee
        const value = parseFloat(exceptionValue.value)
        if (exceptionValue.ignoreNoCico && !working_duration) return false
        if (exceptionValue.units === "hours" && Math.abs(employee.cicoDiff || 0) > value * 60) return true
        else if (exceptionValue.units === "percent" && entered_time_duration && working_duration) {
            if (entered_time_duration > working_duration && Math.abs(1 - working_duration / entered_time_duration) > value / 100) hoursExceed = true
            else if (working_duration && Math.abs(1 - entered_time_duration / working_duration) > value / 100) hoursExceed = true
        }
    }

    return hoursExceed || (editedByOther && exceptionValue.flagOthersEdits) || (anyEdits && exceptionValue.flagAnyEdits)
}

/**
 * Determine whether this cell represents an exception for the amount of hours configured
 * @param exceptionValue Min/max hours that we check against
 * @param cellData The cell/row data
 * @param employee Optional employee. If we have this, we're not in a pivot cell and the computation is
 * a little different.
 * @param isTotalCell Whether the cell is the total cell at the end of the week
 */
export const isHoursException = (exceptionValue: HoursExceptionSettings, cellData: TkRowData, employee?: iEmployee, isTotalCell?: boolean): boolean => {
    // Totals only matter if we're doing the whole week. We want to skip them if we aren't,
    // and we want to skip everything else if we are
    if (isTotalCell !== exceptionValue.weekly) return false

    const minHours = parseFloat(exceptionValue.minHours) || 0
    const maxHours = parseFloat(exceptionValue.maxHours) || 99

    // If we have an employee, we're just looking at a single cell
    if (employee) {
        const enteredHours = ((employee.entered_time_duration || 0) + (employee.localTimeDiff || 0)) / 60
        return enteredHours < minHours || enteredHours > maxHours
    }

    if ((typeof cellData === "object" && !Object.keys(cellData).length) || typeof cellData !== "object") return false

    // If we're split out by date, we're looking at an entire row so we check if every date fits within the
    // hour limits. If not it's an exception
    if (cellData.byDate && Object.keys(cellData.byDate).length && !exceptionValue.weekly) {
        return !Object.values(cellData.byDate).every((data: { ST: number; DT: number; OT: number }) => {
            const enteredHours = (data.ST || 0) + (data.OT || 0) + (data.DT || 0)
            return enteredHours >= minHours && enteredHours <= maxHours
        })
    } else {
        const enteredHours = (cellData.ST || 0) + (cellData.OT || 0) + (cellData.DT || 0)
        const minMaxResult = enteredHours < minHours || enteredHours > maxHours
        const otResult = exceptionValue.minSTHoursEnabled ? (cellData.OT || 0) + (cellData.DT || 0) > 0 && cellData.ST < exceptionValue.minSTHours : false
        return minMaxResult || otResult
    }
}

/**
 * Check that a break exists in a set of start/stop times which is longer than the minimum
 * @param startStopTimes
 * @param minDuration
 */
export const anyMealGreaterThanMinimum = (startStopTimes: EmployeeWorkShiftStartStopTime[], minDuration: number): boolean => {
    return !startStopTimes.find(sst => sst.start_stop_type === "Meal") || !!startStopTimes.find(sst => sst.start_stop_type === "Meal" && sst.duration_minutes >= minDuration)
}

/**
 * Check a set of shifts and breaks to find whether a meal occurs within a given number of minutes of the
 * shift start time.
 * @param startStopTimes List of shift start/stop times for an employee
 * @param numberOfMinutes Threshold in minutes
 */
export const mealWithinMinutesOfShiftStart = (startStopTimes: EmployeeWorkShiftStartStopTime[], numberOfMinutes: number): boolean => {
    const shifts = startStopTimes.filter(sst => !sst.is_break)
    const meals = startStopTimes.sort((sst1, sst2) => (new Date(sst1.start_time) > new Date(sst2.start_time) ? -1 : 1)).filter(sst => sst.start_stop_type === "Meal")
    if (!shifts.length) return true
    return shifts.every(sst => {
        const shiftStartTime = new Date(sst.start_time)
        const shiftEndTime = new Date(sst.stop_time)
        if ((shiftEndTime.getTime() - shiftStartTime.getTime()) / 60000 < numberOfMinutes) return true
        // Find the first meal in the sorted list which is within the requested number of minutes
        return meals.find(m => {
            if (m.start_time < sst.start_time || m.start_time > sst.stop_time) return false
            const mealTime = new Date(m.start_time) as Date
            return (mealTime.getTime() - shiftStartTime.getTime()) / 60000 < numberOfMinutes
        })
    })
}

/**
 * Determine whether this cell represents an exception for any missing Shifts, Breaks, or Meals
 * @param exceptionValue Whether to check for missing Shifts, Breaks, and/or Meals
 * @param cellData The cell data
 * @param isTotalCell Whether the cell is the total cell at the end of the week
 */
export const isShiftsBreaksException = (exceptionValue: ShiftsBreaksExceptionSettings, cellData: TkRowData | "undefined", isTotalCell?: boolean): boolean => {
    if (!cellData || cellData === "undefined" || typeof cellData !== "object" || Object.keys(cellData).length === 0 || isTotalCell) return false

    let isException = false
    // Minimum break duration.
    if (exceptionValue.minMealDurationEnabled) {
        // We make sure that every break in every day in the row is longer than the minimum duration
        if (cellData.byDate && Object.keys(cellData.byDate).length) {
            isException = isException || !Object.values(cellData.byDate).every(data => !data.startStopTimes?.length || anyMealGreaterThanMinimum(data.startStopTimes, exceptionValue.minMealDuration))
        } else {
            // Here we're just dealing with one day (the cell class rule) so make sure that one works.
            if (cellData.startStopTimes?.length) isException = isException || !anyMealGreaterThanMinimum(cellData.startStopTimes, exceptionValue.minMealDuration)
        }
    }
    // Make sure that the Meal has been taken within the specified number of minutes of the shift start
    if (exceptionValue.maxTimeBeforeMealEnabled) {
        if (cellData.byDate && Object.keys(cellData.byDate).length) {
            isException =
                isException || !Object.values(cellData.byDate).every(data => !data.startStopTimes?.length || mealWithinMinutesOfShiftStart(data.startStopTimes, exceptionValue.maxTimeBeforeMeal))
        } else {
            isException = isException || (!!cellData.startStopTimes?.length && !mealWithinMinutesOfShiftStart(cellData.startStopTimes, exceptionValue.maxTimeBeforeMeal))
        }
    }
    if (cellData.byDate && Object.keys(cellData.byDate).length) {
        isException =
            isException ||
            (exceptionValue.missingShift && !Object.values(cellData.byDate).every(data => data.startStopTypes?.has("Shift"))) ||
            (exceptionValue.missingBreak && !Object.values(cellData.byDate).every(data => data.startStopTypes?.has("Break"))) ||
            (exceptionValue.missingMeal && !Object.values(cellData.byDate).every(data => data.startStopTypes?.has("Meal")))
    } else {
        isException =
            isException ||
            (exceptionValue.missingShift && !cellData.startStopTypes?.has("Shift")) ||
            (exceptionValue.missingBreak && !cellData.startStopTypes?.has("Break")) ||
            (exceptionValue.missingMeal && !cellData.startStopTypes?.has("Meal"))
    }
    return isException
}
